import React, { useEffect, useMemo, useState } from 'react';
import { Trip, Booking, BoardingManifest } from '../../types';
import apiService from '../../services/api';
import { DateDisplay } from '../Forms';
import { getApiUrl } from '../../config/environment';

interface TripPassengerManifestProps {
  trip: Trip;
  bookings: Booking[];
  boardingManifest?: BoardingManifest;
  busFilterId?: string | null;
}

interface VendorInfo {
  name?: string;
  companyName?: string;
  logo?: string;
}

type PassengerRow = {
  bookingNumber: string;
  name: string;
  idNumber: string;
  nationality?: string;
  phone: string;
  gender: string;
  isMainContact: boolean;
  tripType: string;
  assignedBus: string | null;
  seatNumber?: string;
};

const MANIFEST_COMPANY_LOGO = '/logo3.png';

const getApiBaseUrl = () => {
  const apiUrl = getApiUrl();
  return apiUrl.replace(/\/api\/?$/, '');
};

const getLogoUrl = (logoPath: string | null | undefined) => {
  if (!logoPath) return null;
  if (logoPath.startsWith('http')) return logoPath;
  return `${getApiBaseUrl()}${logoPath.startsWith('/') ? logoPath : `/${logoPath}`}`;
};

const boardingStatusLabel = (status: string) => {
  if (status === 'boarded') return 'صعد';
  if (status === 'no-show') return 'لم يحضر';
  return 'لم يصعد';
};

const statusClass = (status: string) => {
  if (status === 'boarded') return 'text-emerald-700';
  if (status === 'no-show') return 'text-red-600';
  return 'text-amber-700';
};

const TripPassengerManifest: React.FC<TripPassengerManifestProps> = ({
  trip,
  bookings,
  boardingManifest,
}) => {
  const [vendorInfo, setVendorInfo] = useState<VendorInfo>({});
  const [logoSrc, setLogoSrc] = useState(MANIFEST_COMPANY_LOGO);
  const printDate = useMemo(() => new Date(), []);
  const useBoarding = !!boardingManifest;

  useEffect(() => {
    const fetchVendorData = async () => {
      try {
        const vendorData = await apiService.getVendorProfile();
        setVendorInfo({
          name: vendorData?.name,
          companyName: vendorData?.companyName,
          logo: vendorData?.logo,
        });
      } catch {
        setVendorInfo({});
      }
    };
    fetchVendorData();
  }, []);

  useEffect(() => {
    const vendorLogo = getLogoUrl(vendorInfo.logo);
    setLogoSrc(vendorLogo || MANIFEST_COMPANY_LOGO);
  }, [vendorInfo.logo]);

  const allPassengers: PassengerRow[] = bookings.flatMap((booking) => {
    const rows: PassengerRow[] = [
      {
        bookingNumber: booking.bookingNumber,
        name: booking.contactPerson.name,
        idNumber: booking.contactPerson.idNumber,
        nationality: booking.contactPerson.nationality,
        phone: booking.contactPerson.phone,
        gender: booking.contactPerson.gender,
        isMainContact: true,
        tripType: booking.tripType,
        assignedBus: booking.assignedBus?.busNumber || null,
        seatNumber: booking.contactPerson.seatNumber,
      },
    ];
    (booking.companions || []).forEach((companion) => {
      rows.push({
        bookingNumber: booking.bookingNumber,
        name: companion.name,
        idNumber: companion.idNumber,
        nationality: companion.nationality,
        phone: companion.phone || '',
        gender: companion.gender,
        isMainContact: false,
        tripType: booking.tripType,
        assignedBus: booking.assignedBus?.busNumber || null,
        seatNumber: companion.seatNumber,
      });
    });
    return rows;
  });

  const busPassengerMap: Record<string, PassengerRow[]> = {};
  let unassignedPassengers: PassengerRow[] = [];
  if (trip.buses && trip.buses.length > 0) {
    trip.buses.forEach((busObj) => {
      const busNumber = busObj.bus.busNumber;
      busPassengerMap[busNumber] = allPassengers.filter((p) => p.assignedBus === busNumber);
    });
    unassignedPassengers = allPassengers.filter(
      (p) => !p.assignedBus || !trip.buses.some((b) => b.bus.busNumber === p.assignedBus)
    );
  }

  const showSingleTable = !trip.buses || trip.buses.length === 0;
  const title = useBoarding ? 'كشف ركاب الباص' : 'كشف المسافرين';
  const counts = boardingManifest?.counts;
  const route = boardingManifest?.trip?.route;
  const bus = boardingManifest?.bus;

  const driverName = useMemo(() => {
    const fromManifest = boardingManifest?.bus?.driver?.name?.trim();
    if (fromManifest) return fromManifest;

    const busId = boardingManifest?.bus?.id;
    if (busId && trip.buses?.length) {
      const match = trip.buses.find(
        (b) => String(b.bus?._id || b.bus) === String(busId)
      );
      if (match?.driver?.name?.trim()) return match.driver.name.trim();
    }

    if (trip.buses?.length === 1 && trip.buses[0]?.driver?.name?.trim()) {
      return trip.buses[0].driver.name.trim();
    }

    return trip.driver?.name?.trim() || '';
  }, [boardingManifest?.bus, trip.buses, trip.driver]);

  const tripRoute =
    route ||
    (trip.routes?.[0]
      ? { from: trip.routes[0].from, to: trip.routes[0].to }
      : trip.route
        ? { from: trip.route.from, to: trip.route.to }
        : null);

  const thClass =
    'px-3 py-2.5 text-right text-xs font-semibold text-gray-600 border-b border-gray-200 bg-gray-50 whitespace-nowrap';
  const tdClass = 'px-3 py-2.5 text-sm text-gray-800 border-b border-gray-100';

  const renderBoardingTable = () => {
    const rows = boardingManifest?.passengers || [];
    return (
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className={`${thClass} text-center w-10`}>#</th>
              <th className={`${thClass} text-center w-16`}>المقعد</th>
              <th className={thClass}>الاسم</th>
              <th className={thClass}>الهوية</th>
              <th className={thClass}>الجوال</th>
              <th className={thClass}>الحجز</th>
              <th className={`${thClass} text-center`}>الصعود</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((passenger) => (
              <tr
                key={`${passenger.bookingNumber}-${passenger.seatNumber}-${passenger.index}`}
                className={
                  passenger.boardingStatus === 'boarded' ? 'bg-emerald-50/60' : 'bg-white'
                }
              >
                <td className={`${tdClass} text-center text-gray-500`}>{passenger.index}</td>
                <td className={`${tdClass} text-center font-semibold`}>
                  {passenger.seatNumber || '—'}
                </td>
                <td className={`${tdClass} font-medium`}>{passenger.name}</td>
                <td className={tdClass}>{passenger.idNumber || '—'}</td>
                <td className={tdClass}>{passenger.phone || '—'}</td>
                <td className={tdClass}>{passenger.bookingNumber}</td>
                <td
                  className={`${tdClass} text-center font-medium ${statusClass(
                    passenger.boardingStatus
                  )}`}
                >
                  {boardingStatusLabel(passenger.boardingStatus)}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                  لا يوجد ركاب مسجّلون على هذا الباص
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderLegacyRow = (passenger: PassengerRow, index: number) => (
    <tr key={`${passenger.bookingNumber}-${passenger.idNumber}-${index}`} className="bg-white">
      <td className={`${tdClass} text-center text-gray-500`}>{index + 1}</td>
      <td className={`${tdClass} text-center font-semibold`}>{passenger.seatNumber || '—'}</td>
      <td className={`${tdClass} font-medium`}>
        {passenger.name}
        {passenger.isMainContact && (
          <span className="mr-1.5 text-[10px] text-gray-400">مسؤول</span>
        )}
      </td>
      <td className={tdClass}>{passenger.idNumber}</td>
      <td className={tdClass}>{passenger.phone || '—'}</td>
      <td className={tdClass}>{passenger.bookingNumber}</td>
      <td className={`${tdClass} w-24`}>
        <div className="h-6 border-b border-gray-300" />
      </td>
    </tr>
  );

  const legacyHead = (
    <thead>
      <tr>
        <th className={`${thClass} text-center w-10`}>#</th>
        <th className={`${thClass} text-center w-16`}>المقعد</th>
        <th className={thClass}>الاسم</th>
        <th className={thClass}>الهوية</th>
        <th className={thClass}>الجوال</th>
        <th className={thClass}>الحجز</th>
        <th className={thClass}>توقيع</th>
      </tr>
    </thead>
  );

  const renderLegacyTable = (rows: PassengerRow[], heading?: string) => (
    <div className="mb-6 last:mb-0">
      {heading && (
        <h3 className="text-sm font-semibold text-gray-700 mb-2">{heading}</h3>
      )}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          {legacyHead}
          <tbody>
            {rows.length > 0 ? (
              rows.map((p, i) => renderLegacyRow(p, i))
            ) : (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  لا يوجد مسافرون
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @media print {
          body { margin: 0 !important; padding: 0 !important; }
          @page { margin: 10mm !important; size: A4; }
          .manifest-container { padding: 0 !important; margin: 0 !important; box-shadow: none !important; }
          .no-print { display: none !important; }
          .manifest-logo { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
      <div
        className="manifest-container bg-white p-6 md:p-8"
        style={{ direction: 'rtl' }}
      >
        {/* Header */}
        <div className="pb-5 mb-5 border-b border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <img
              src={logoSrc}
              alt=""
              className="manifest-logo w-12 h-12 object-contain shrink-0"
              onError={() => setLogoSrc(MANIFEST_COMPANY_LOGO)}
            />
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-gray-900 leading-tight">{title}</h1>
              <p className="text-sm text-gray-500 truncate">
                {vendorInfo.companyName || vendorInfo.name || 'الشركة'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 rounded-lg bg-gray-50 border border-gray-100 px-4 py-3">
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="text-xs text-gray-500 shrink-0 w-14">الرحلة</span>
              <span className="text-sm font-semibold text-gray-900 tracking-wide">
                {trip.tripNumber}
              </span>
            </div>

            {tripRoute && (
              <div className="flex items-baseline gap-2 min-w-0">
                <span className="text-xs text-gray-500 shrink-0 w-14">المسار</span>
                <span className="text-sm font-medium text-gray-900">
                  {tripRoute.from}
                  <span className="text-gray-400 mx-1.5">→</span>
                  {tripRoute.to}
                </span>
              </div>
            )}

            {bus && (
              <div className="flex items-baseline gap-2 min-w-0">
                <span className="text-xs text-gray-500 shrink-0 w-14">الباص</span>
                <span className="text-sm font-medium text-gray-900">
                  {bus.busNumber}
                  {bus.plateNumber ? (
                    <span className="text-gray-500 font-normal"> · {bus.plateNumber}</span>
                  ) : null}
                </span>
              </div>
            )}

            {driverName ? (
              <div className="flex items-baseline gap-2 min-w-0">
                <span className="text-xs text-gray-500 shrink-0 w-14">السائق</span>
                <span className="text-sm font-medium text-gray-900">{driverName}</span>
              </div>
            ) : null}

            <div className="flex items-baseline gap-2 min-w-0">
              <span className="text-xs text-gray-500 shrink-0 w-14">طباعة</span>
              <div className="text-sm text-gray-800 min-w-0">
                <DateDisplay
                  value={printDate}
                  showTime
                  dualLayout="inline"
                  dualSeparator=" · "
                  className="inline-flex flex-wrap items-baseline gap-x-1"
                  gregorianClassName="text-sm text-gray-900"
                  hijriClassName="text-sm text-gray-600"
                />
                {boardingManifest?.printedBy?.fullName ? (
                  <span className="text-xs text-gray-500 block mt-0.5">
                    بواسطة {boardingManifest.printedBy.fullName}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {/* Counts */}
        {counts && (
          <div className="flex flex-wrap gap-3 mb-5 text-sm">
            <span className="px-3 py-1 rounded-md bg-gray-100 text-gray-800">
              الإجمالي: <strong>{counts.total}</strong>
            </span>
            <span className="px-3 py-1 rounded-md bg-emerald-50 text-emerald-800">
              صعد: <strong>{counts.boarded}</strong>
            </span>
            <span className="px-3 py-1 rounded-md bg-amber-50 text-amber-800">
              متبقي: <strong>{counts.remaining}</strong>
            </span>
          </div>
        )}

        {/* Tables */}
        {useBoarding ? (
          renderBoardingTable()
        ) : showSingleTable ? (
          renderLegacyTable(allPassengers)
        ) : (
          <>
            {trip.buses.map((busObj) =>
              renderLegacyTable(
                busPassengerMap[busObj.bus.busNumber] || [],
                `الحافلة ${busObj.bus.busNumber}`
              )
            )}
            {unassignedPassengers.length > 0 &&
              renderLegacyTable(unassignedPassengers, 'بدون حافلة')}
          </>
        )}
      </div>
    </>
  );
};

export default TripPassengerManifest;
